import type { TabName } from '@types';
import { cn } from '@utils';
import { motion, AnimatePresence } from 'framer-motion';

// Import icons
import questsIcon from '@/assets/bottom-navbar/quests.webp';
import shopIcon from '@/assets/bottom-navbar/shop.webp';
import battleIcon from '@/assets/bottom-navbar/battle.webp';
import frensIcon from '@/assets/bottom-navbar/frens.webp';
import rankingIcon from '@/assets/bottom-navbar/ranking.webp';

export interface BottomNavBarProps {
  activeTab: TabName;
  onTabChange: (tabName: TabName) => void;
  isFirstMount?: boolean;
}

interface NavItemProps {
  iconSrc: string;
  label: string;
  isActive: boolean;
  onClick: () => void;
  isFirstMount?: boolean;
}

const NavItem = ({
  iconSrc,
  label,
  isActive,
  onClick,
  isFirstMount = false,
}: NavItemProps) => {
  return (
    <motion.button
      onClick={onClick}
      className={cn(
        'relative flex flex-col items-center justify-center',
        'z-[5]'
      )}
      style={{
        flex: isActive ? '1.108' : '1',
        minHeight: isActive ? '90px' : '76px',
      }}
      whileHover={{ scale: 1.05, y: -2 }}
      whileTap={{ scale: 0.95 }}
      animate={isActive && !isFirstMount ? { y: [-8, 0] } : { y: 0 }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 17,
      }}
    >
      {/* Background with GLOW effect when active */}
      <motion.div
        className={cn(
          'absolute left-0 right-0 bottom-0 rounded-xl border-2 border-black transition-colors duration-300',
          isActive ? 'top-3 bg-[#B61AF6]' : 'top-3 bg-[#8600BD]'
        )}
        style={{
          boxSizing: 'border-box',
        }}
        animate={
          isActive && !isFirstMount
            ? {
                boxShadow: [
                  '0 0 20px rgba(182, 26, 246, 0.6), 0 0 40px rgba(182, 26, 246, 0.3)',
                  '0 0 30px rgba(182, 26, 246, 0.8), 0 0 50px rgba(182, 26, 246, 0.4)',
                  '0 0 20px rgba(182, 26, 246, 0.6), 0 0 40px rgba(182, 26, 246, 0.3)',
                ],
              }
            : { boxShadow: '0 0 0px rgba(0, 0, 0, 0)' }
        }
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        {/* Inner shine with PULSE animation */}
        <div
          className="absolute left-1/2 -translate-x-1/2 w-[90%] h-7 rounded-lg"
          style={{
            top: '4px',
            background: 'rgba(255, 255, 255, 0.2)',
          }}
        />
      </motion.div>

      {/* Icon with BOUNCE and ROTATE when active */}
      <motion.div
        className={cn('absolute', isActive ? 'h-[43.89px]' : 'h-[41.11px]')}
        style={{
          left: '22.22%',
          right: '22.22%',
          top: '0px',
        }}
        animate={
          isActive && !isFirstMount
            ? {
                rotate: [0, -10, 10, -5, 5, 0],
                scale: [1, 1.1, 1],
              }
            : { rotate: 0, scale: 1 }
        }
        transition={{
          duration: 0.6,
          ease: 'easeOut',
        }}
      >
        <img
          src={iconSrc}
          alt={label}
          className="w-full h-full object-contain"
          style={{
            position: 'absolute',
            left: '0%',
            right: '0%',
            top: '0%',
            bottom: '0%',
            filter: isActive
              ? 'drop-shadow(0 4px 8px rgba(182, 26, 246, 0.6))'
              : 'none',
          }}
        />
      </motion.div>

      {/* Label with SCALE animation */}
      <motion.span
        className="absolute left-0 right-0 text-center text-white"
        style={{
          fontFamily: 'Lilita One',
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: isActive ? '22px' : '19px',
          lineHeight: '21px',
          height: '13px',
          bottom: isActive ? '18px' : '14px',
          WebkitTextStroke: '1px #000',
          textShadow: isActive ? '0 0 10px rgba(182, 26, 246, 0.8)' : 'none',
        }}
        animate={isActive && !isFirstMount ? { scale: [1, 1.05, 1] } : { scale: 1 }}
        transition={{
          duration: 0.3,
        }}
      >
        {label}
      </motion.span>

      {/* PARTICLE BURST effect when active */}
      <AnimatePresence>
        {isActive && !isFirstMount && (
          <>
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full"
                style={{
                  width: '4px',
                  height: '4px',
                  background: 'rgba(182, 26, 246, 0.8)',
                  left: '50%',
                  top: '30%',
                }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                  opacity: [0, 1, 0],
                  scale: [0, 1, 0],
                  x: [0, Math.cos((i * Math.PI) / 3) * 20],
                  y: [0, Math.sin((i * Math.PI) / 3) * 20],
                }}
                transition={{
                  duration: 0.6,
                  ease: 'easeOut',
                  delay: i * 0.05,
                }}
              />
            ))}
          </>
        )}
      </AnimatePresence>
    </motion.button>
  );
};

export const BottomNavBar = ({
  activeTab,
  onTabChange,
  isFirstMount = false,
}: BottomNavBarProps) => {
  const navItems: Array<{ tab: TabName; iconSrc: string; label: string }> = [
    { tab: 'quests', iconSrc: questsIcon, label: 'Quests' },
    { tab: 'shop', iconSrc: shopIcon, label: 'Shop' },
    { tab: 'battle', iconSrc: battleIcon, label: 'Battle' },
    { tab: 'frens', iconSrc: frensIcon, label: 'Frens' },
    { tab: 'ranking', iconSrc: rankingIcon, label: 'Ranking' },
  ];

  return (
    <motion.nav
      className="w-full bg-gradient-to-t from-indigo-950 via-indigo-900/80 to-transparent pt-6 px-1 pb-1"
      initial={isFirstMount ? { opacity: 0 } : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        isFirstMount
          ? { duration: 0.3, ease: 'easeOut', delay: 0.9 }
          : { duration: 0.5, ease: 'easeOut', delay: 0.6 }
      }
    >
      <div className="flex items-end justify-center w-full">
        {navItems.map(({ tab, iconSrc, label }) => (
          <NavItem
            key={tab}
            iconSrc={iconSrc}
            label={label}
            isActive={activeTab === tab}
            onClick={() => onTabChange(tab)}
            isFirstMount={isFirstMount}
          />
        ))}
      </div>
    </motion.nav>
  );
};
